/**
 * QuickTapsAddEditPageV2 — V2 edit-page chrome for Quick Taps.
 * Sibling of SessionsAddEditPageV2: sticky `edit-header`, eyebrow +
 * H1 + id sub-line + Discard / Duplicate / Save actions; the form
 * itself lives in AddEditQuickTapV2 (right-rail layout included).
 */
import React, { useCallback, useState } from 'react';
import { useLocation, useParams, Link, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import Swal from 'sweetalert2';
import AddEditQuickTapV2 from '../../AddEditQuickTapSession/v2/AddEditQuickTapV2';
import { duplicateQuickTapSession } from '../../../actions/quickTapsActions';
import { Button, cn } from '../../redesign';

/* ─── Icons ────────────────────────────────────────────────────────── */

const SaveIconSvg = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
    <polyline points="17 21 17 13 7 13 7 21" />
    <polyline points="7 3 7 8 15 8" />
  </svg>
);

const CopyIconSvg = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

/* ─── Main ─────────────────────────────────────────────────────────── */

export default function QuickTapsAddEditPageV2() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();
  const isEdit = location.pathname.includes('edit');
  const [isDuplicating, setIsDuplicating] = useState(false);

  /* Surface the title in the header for edit mode. The form fires
   * the only GET /quick-tap-sessions/{id} call and hands the title
   * + updated_at back via `onLoaded`. */
  const [quickTapTitle, setQuickTapTitle] = useState(null);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const handleFormLoaded = useCallback(({ title, updated_at }) => {
    setQuickTapTitle(title || null);
    setLastSavedAt(updated_at || null);
  }, []);

  const handleDuplicate = async () => {
    const { isConfirmed } = await Swal.fire({
      title: 'Duplicate Quick Tap?',
      text: 'This will create a copy of this quick tap (inactive by default).',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Duplicate',
    });
    if (!isConfirmed) return;
    try {
      setIsDuplicating(true);
      const newQuickTap = await dispatch(duplicateQuickTapSession(id));
      Swal.fire({
        icon: 'success',
        title: 'Quick Tap Duplicated!',
        text: `Created: ${newQuickTap.title}`,
        timer: 1500,
      }).then(() => {
        navigate(`/quick-taps/edit/${newQuickTap.id}`);
      });
    } catch (err) {
      console.error('Error duplicating quick tap:', err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to duplicate quick tap. Please try again.',
      });
    } finally {
      setIsDuplicating(false);
    }
  };

  const lastSavedLabel = lastSavedAt
    ? `last saved ${new Date(lastSavedAt).toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })}`
    : null;

  const eyebrow = isEdit ? 'Editing quick tap' : 'New quick tap';
  const title = isEdit
    ? (quickTapTitle || `Quick Tap #${id}`)
    : 'Untitled quick tap';

  return (
    <div className="bg-bg min-h-full">
      <div
        className="sticky top-0 bg-bg border-b border-sand"
        style={{ zIndex: 20 }}
      >
        <div
          className="flex items-start"
          style={{ padding: '20px 28px', gap: 16, flexWrap: 'wrap' }}
        >
          <div className="flex-1 min-w-0">
            <div
              className="font-condensed font-bold uppercase text-text-muted"
              style={{ fontSize: 11, letterSpacing: '0.08em' }}
            >
              {eyebrow}
            </div>
            <h1
              className={cn(
                'text-navy truncate',
                !quickTapTitle && isEdit && 'text-text-light italic',
              )}
              style={{
                fontSize: 28,
                fontWeight: 700,
                margin: '4px 0 4px',
                letterSpacing: '-0.015em',
                lineHeight: 1.15,
              }}
              title={title}
            >
              {title}
            </h1>
            <div
              className="font-mono text-text-muted"
              style={{ fontSize: 12 }}
            >
              {isEdit ? (
                <>
                  quick_tap_id · <strong className="text-navy">{id}</strong>
                  {lastSavedLabel && (
                    <>
                      {' · '}
                      <span>{lastSavedLabel}</span>
                    </>
                  )}
                </>
              ) : (
                <span>Fill the fields below and click Create.</span>
              )}
            </div>
          </div>

          <div className="flex items-center" style={{ gap: 8 }}>
            <Link to="/quick-taps" className="no-underline">
              <Button variant="ghost">Discard Changes</Button>
            </Link>
            {isEdit && (
              <Button
                variant="default"
                onClick={handleDuplicate}
                disabled={isDuplicating}
                leadingIcon={CopyIconSvg}
              >
                {isDuplicating ? 'Duplicating…' : 'Duplicate'}
              </Button>
            )}
            <Button
              variant="coral"
              type="submit"
              form="quick-tap-form"
              leadingIcon={SaveIconSvg}
            >
              {isEdit ? 'Save' : 'Create'}
            </Button>
          </div>
        </div>
      </div>

      <div style={{ padding: '24px 32px 60px' }}>
        <AddEditQuickTapV2 isEdit={isEdit} selectedId={id} onLoaded={handleFormLoaded} />
      </div>
    </div>
  );
}
